import { Box, Grid, Typography } from '@mui/material'
import { about } from '@/content/copy'
import { featureIcons } from '@/lib/icons'
import { colors, fonts, layout } from '@/theme'
import { Section } from '@/components/ui/Section'
import { SectionHeading } from '@/components/ui/SectionHeading'
import { Eyebrow } from '@/components/ui/Eyebrow'
import { FeatureCard } from '@/components/cards/FeatureCard'
import { Reveal } from '@/components/motion/Reveal'
import { StatsBand } from '@/components/sections/StatsBand'
import { ContactCTA } from '@/components/sections/ContactCTA'
import { Seo } from '@/components/seo/Seo'

export function About() {
  return (
    <>
      <Seo
        title="About Us"
        path="/about"
        description="True Forge manufactures premium welding gloves, driver gloves and leather garments — engineered for the hazard, built to international standards, and trusted across heavy industry."
      />

      <Section sx={{ pb: 0 }}>
        <Eyebrow>{about.eyebrow}</Eyebrow>
        <Typography variant="h1" component="h1" sx={{ maxWidth: 940 }}>
          {about.title}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mt: 3, maxWidth: layout.contentMaxWidth }}>
          {about.lede}
        </Typography>
      </Section>

      <Section>
        <Grid container spacing={{ xs: 3, md: 8 }}>
          {about.story.map((para) => (
            <Grid key={para.slice(0, 24)} size={{ xs: 12, md: 6 }}>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                {para}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* Mission pull-quote */}
      <Section sx={{ pt: 0 }}>
        <Box sx={{ borderLeft: `2px solid ${colors.gold}`, pl: { xs: 3, md: 5 }, maxWidth: 900 }}>
          <Eyebrow>Our mission</Eyebrow>
          <Typography
            variant="h4"
            component="p"
            sx={{ fontFamily: fonts.body, fontWeight: 500, lineHeight: 1.4, color: colors.white }}
          >
            {about.mission}
          </Typography>
        </Box>
      </Section>

      <StatsBand />

      {/* Journey timeline */}
      <Section>
        <SectionHeading eyebrow="Our journey" title="How True Forge is built" />
        <Box sx={{ maxWidth: 840 }}>
          {about.journey.map((step, i) => {
            const isLast = i === about.journey.length - 1
            return (
              <Reveal key={step.phase} y={16}>
                <Box sx={{ display: 'flex', gap: { xs: 2.5, md: 4 }, alignItems: 'stretch' }}>
                  {/* Rail: node + connecting line */}
                  <Box sx={{ position: 'relative', width: 48, flexShrink: 0 }}>
                    {!isLast && (
                      <Box sx={{ position: 'absolute', top: 48, bottom: 0, left: '50%', ml: '-0.5px', width: '1px', bgcolor: colors.border }} />
                    )}
                    <Box
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        border: `1px solid ${colors.borderStrong}`,
                        bgcolor: colors.surface,
                        display: 'grid',
                        placeItems: 'center',
                        color: colors.gold,
                        fontFamily: fonts.display, fontWeight: 600,
                        fontSize: 22,
                        lineHeight: 1,
                      }}
                    >
                      {step.phase}
                    </Box>
                  </Box>

                  {/* Content */}
                  <Box sx={{ pb: isLast ? 0 : { xs: 4, md: 5 }, pt: 0.75 }}>
                    <Typography variant="h5" component="h3">
                      {step.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1.5, maxWidth: 620 }}>
                      {step.text}
                    </Typography>
                  </Box>
                </Box>
              </Reveal>
            )
          })}
        </Box>
      </Section>

      {/* Principles */}
      <Section sx={{ pt: 0 }}>
        <SectionHeading eyebrow="What we stand for" title="Our principles" />
        <Grid container spacing={{ xs: 4, md: 5 }}>
          {about.values.map((value) => (
            <Grid key={value.title} size={{ xs: 12, sm: 6, md: 4 }}>
              <FeatureCard icon={featureIcons[value.icon]} title={value.title} text={value.text} />
            </Grid>
          ))}
        </Grid>
      </Section>

      <ContactCTA />
    </>
  )
}
